/**
 * Utility per la gestione degli appuntamenti
 */


/**
 * Ottiene la classe CSS per il badge di stato dell'appuntamento
 * @param {string} status - Lo stato dell'appuntamento
 * @return {string} Classe CSS per il badge
 */
export const getStatusBadgeClass = (status) => {
  switch (status) {
    case "confirmed": return "bg-green-100 text-green-800";
    case "booked": return "bg-yellow-100 text-yellow-800";
    case "suggested": return "bg-purple-100 text-purple-800";
    case "cancelled": return "bg-gray-100 text-gray-800";
    default: return "bg-gray-100 text-gray-800";
  }
};


export const getStatusLabel = (status, daysRemaining) => {
  // Se l'appuntamento è "confirmed" ma nel passato, mostra "Svolta"
  if (status === "confirmed" && daysRemaining < 0) {
    return "Svolta";
  }
  
  // Altrimenti, usa le label standard
  switch (status) {
    case "booked": return "Da pagare";
    case "confirmed": return "Confermata";
    case "suggested": return "Suggerita";
    case "cancelled": return "Annullata";
    default: return status;
  }
};

export const getStatusColor = (status, daysRemaining) => {
  const statusLabel = getStatusLabel(status, daysRemaining);
  
  switch (statusLabel) {
    case "Svolta":
      return "bg-blue-100 text-blue-800";
    case "Confermata":
      return "bg-green-100 text-green-800";
    case "Da pagare":
      return "bg-yellow-100 text-yellow-800";
    case "Suggerita":
      return "bg-purple-100 text-purple-800";
    case "Annullata":
      return "bg-gray-100 text-gray-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

/**
 * Controlla se un appuntamento è attivo (non cancellato)
 * @param {string} status - Lo stato dell'appuntamento
 * @param {number} daysRemaining - Giorni rimanenti all'appuntamento (negativo se nel passato)
 * @return {boolean} True se l'appuntamento è attivo
 */
export const isAppointmentActive = (status, daysRemaining) => {
  // Un appuntamento è considerato attivo se:
  // - Non è cancellato
  // - Se è confermato, è ancora nel futuro (daysRemaining >= 0)
  return status !== "cancelled" && !(status === "confirmed" && daysRemaining < 0);
};

/**
 * Controlla se un appuntamento è modificabile
 * @param {string} status - Lo stato dell'appuntamento
 * @param {number} daysRemaining - Giorni rimanenti all'appuntamento
 * @return {boolean} True se l'appuntamento è modificabile
 */
export const isAppointmentEditable = (status, daysRemaining) => {
  // Un appuntamento è modificabile se:
  // - È in stato booked, confirmed o suggested
  // - Non è nel passato
  return ["booked", "confirmed", "suggested"].includes(status) && daysRemaining >= 0;
};

/**
 * Controlla se un appuntamento è in attesa di azione da parte del paziente
 * @param {string} status - Lo stato dell'appuntamento
 * @return {boolean} True se l'appuntamento richiede azione
 */
export const requiresPatientAction = (status) => {
  // Un appuntamento richiede azione del paziente se:
  // - È in attesa di pagamento (booked)
  // - È stato suggerito dal terapeuta (suggested)
  return ["booked", "suggested"].includes(status);
};

/**
 * Controlla se un appuntamento può essere cancellato
 * @param {string} status - Lo stato dell'appuntamento
 * @param {number} daysRemaining - Giorni rimanenti all'appuntamento
 * @return {boolean} True se l'appuntamento può essere cancellato
 */
export const canCancelAppointment = (status, daysRemaining) => {
  // Un appuntamento può essere cancellato se:
  // - È attivo (non già cancellato o passato)
  // - Non è nel passato
  return isAppointmentActive(status, daysRemaining) && daysRemaining >= 0;
};

/**
 * Controlla se un appuntamento è completato (confermato e nel passato)
 * @param {string} status - Lo stato dell'appuntamento
 * @param {number} daysRemaining - Giorni rimanenti all'appuntamento
 * @return {boolean} True se l'appuntamento è completato
 */
export const isAppointmentCompleted = (status, daysRemaining) => {
  return status === "confirmed" && daysRemaining < 0;
};

